import { useEnum } from "@/api/services/global/enum";
import { TGetEnumOptionsDto } from "@/api/services/global/enum/dtos/get-enum-options.dto";
import { TGetExamDetailDto } from "@/api/services/main/learning-progress/dtos/exam/get-exam-detail-by-industry.dto";
import { ExamProcedureList } from "@/features/learning-progress/components/exam/ExamProcedureList";
import { DATE_FORMAT, WEEKDAY_FORMAT, YEAR_MONTH_FORMAT } from "@/constants/datetime";
import { EXAM_PROCEDURE_TYPE, EXAM_VIRTUAL_PROCEDURE_ID } from "@/features/learning-progress/constants/exam-schedule";
import {
  getInitialProcedureId,
  getProcedureDescription,
  parseTextWithLinks,
} from "@/features/learning-progress/utils/exam-schedule";
import { background, primary, state, text } from "@/theme/colors";
import { findLabelByValue } from "@/utils/object";
import { OpenInNew } from "@mui/icons-material";
import { Stack, styled, Typography } from "@mui/material";
import dayjs from "dayjs";
import React, { FC, useEffect, useMemo, useState } from "react";

type TExamProcedureType = (typeof EXAM_PROCEDURE_TYPE)[keyof typeof EXAM_PROCEDURE_TYPE];

type TRawProcedureItem = {
  id: number;
  stepName: string;
  endDate: string | null;
  duration: string | null;
  type: TExamProcedureType;
};

function buildRawProcedureItems(exam: TGetExamDetailDto): TRawProcedureItem[] {
  return [
    ...exam.procedures.map((p) => ({
      id: p.id,
      stepName: p.stepName,
      endDate: p.endDate,
      duration: p.duration,
      type: EXAM_PROCEDURE_TYPE.PROCEDURE,
    })),
    {
      id: EXAM_VIRTUAL_PROCEDURE_ID.EXAM_IMPLEMENTATION,
      stepName: "試験実施",
      endDate: exam.examMonth,
      duration: null,
      type: EXAM_PROCEDURE_TYPE.EXAM_IMPLEMENTATION,
    },
    {
      id: EXAM_VIRTUAL_PROCEDURE_ID.RESULT_DETERMINATION,
      stepName: "合否判定",
      endDate: null,
      duration: null,
      type: EXAM_PROCEDURE_TYPE.RESULT_DETERMINATION,
    },
  ];
}

function isItemPast(item: TRawProcedureItem, isPaperTest: boolean): boolean {
  if (!isPaperTest || !item.endDate) return false;

  const now = dayjs();
  if (item.type === EXAM_PROCEDURE_TYPE.EXAM_IMPLEMENTATION) {
    return dayjs(item.endDate).isBefore(now, "month");
  }
  return dayjs(item.endDate).isBefore(now, "day");
}

function buildDeadline(
  item: TRawProcedureItem,
  exam: TGetExamDetailDto,
  isPaperTest: boolean,
  isCBT: boolean,
  disabled: boolean,
): React.ReactNode {
  if (item.type === EXAM_PROCEDURE_TYPE.RESULT_DETERMINATION) {
    if (!exam.announcementEstimate) return null;
    const variant = isPaperTest ? "body14Semi" : "body14Bold";
    return (
      <Typography variant={variant} color={disabled ? text.disable : text.accent_orange}>
        {exam.announcementEstimate}
      </Typography>
    );
  }

  if (item.type === EXAM_PROCEDURE_TYPE.EXAM_IMPLEMENTATION) {
    if (!item.endDate) return null;
    return (
      <Typography variant="body14Semi" color={disabled ? text.disable : text.accent_orange}>
        {dayjs(item.endDate).format(YEAR_MONTH_FORMAT)}
      </Typography>
    );
  }

  // Regular procedure
  if (isPaperTest && item.endDate) {
    const date = dayjs(item.endDate).locale("ja");
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        <Typography variant="body14Semi" color={disabled ? text.disable : text.accent_orange}>
          {date.format(DATE_FORMAT)}({date.format(WEEKDAY_FORMAT)})
        </Typography>
        <Typography variant="body14" color={disabled ? text.disable : text.secondary}>
          まで
        </Typography>
      </Stack>
    );
  }

  if (isCBT && item.duration) {
    return (
      <Typography variant="body14Bold" color={text.accent_orange}>
        {item.duration}
      </Typography>
    );
  }

  return null;
}

function processExamProcedures(
  exam: TGetExamDetailDto,
  agentOptions: TGetEnumOptionsDto[],
) {
  if (!exam.procedures) return [];

  const isPaperTest = exam.examMethod === "paper_test";
  const isCBT = exam.examMethod === "cbt";
  const allItems = buildRawProcedureItems(exam);
  const agentByProcedureId = new Map(exam.procedures.map((p) => [p.id, p.agent]));

  return allItems.map((item) => {
    const isPast = isItemPast(item, isPaperTest);
    const deadline = buildDeadline(item, exam, isPaperTest, isCBT, isPast);
    const agent = agentByProcedureId.get(item.id);
    const agentLabel = agent ? findLabelByValue(agentOptions, agent) : null;

    return {
      id: item.id,
      stepName: item.stepName,
      deadline,
      agentLabel,
      isPast,
    };
  });
}

export const ExamScheduleTab: FC<{ exam: TGetExamDetailDto | null }> = ({ exam }) => {
  const [selectedProcedureId, setSelectedProcedureId] = useState<number | null>(() => getInitialProcedureId(exam));
  const { data: agentOptions } = useEnum("EExamProcedureAgent");

  useEffect(() => {
    setSelectedProcedureId(getInitialProcedureId(exam));
  }, [exam]);

  const { description, site } = useMemo(
    () => getProcedureDescription(exam, selectedProcedureId),
    [exam, selectedProcedureId],
  );

  const processedProcedures = useMemo(() => {
    if (!exam) return [];
    return processExamProcedures(exam, agentOptions);
  }, [exam, agentOptions]);

  return (
    <Stack gap={1.5}>
      {processedProcedures.length > 0 ? (
        <>
          <ExamProcedureList
            procedures={processedProcedures}
            selectedProcedureId={selectedProcedureId}
            onSelectProcedure={setSelectedProcedureId}
          />
          {(description || site) && (
            <Stack
              gap={1.25}
              borderRadius={1.5}
              py={2}
              px={3}
              bgcolor={background.secondary}
              border={`1px solid ${primary[500]}`}
            >
              {description && <Typography variant="body14" whiteSpace="pre-line">
                {parseTextWithLinks(description).map((part) =>
                  part.type === "link" ? (
                    <ExternalSiteLink href={part.content} text={part.content} key={part.key} />
                  ) : (
                    <React.Fragment key={part.key}>{part.content}</React.Fragment>
                  ),
                )}
              </Typography>}
              {site && (
                <Stack flexDirection="row">
                  <ExternalSiteLink href={site} text={site} />
                </Stack>
              )}
          </Stack>)}
        </>
      ) : (
        <Typography variant="body14">
          スケジュール情報がありません。
        </Typography>
      )}
    </Stack>
  );
};

const StyledExternalLink = styled("a")({
  textDecoration: "none",
});

type ExternalSiteLinkProps = {
  href: string;
  text: string;
};

const ExternalSiteLink: FC<ExternalSiteLinkProps> = ({ href, text }) => (
  <StyledExternalLink href={href} target="_blank" rel="noopener noreferrer">
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Typography variant="body14" color={state.link_1}>
        {text}
      </Typography>
      <OpenInNew sx={{ fontSize: 16, color: state.link_1 }} />
    </Stack>
  </StyledExternalLink>
);
